import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import PendingRoundedIcon from "@mui/icons-material/PendingRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { completePayment } from "../../api/api";

const MAX_STATUS_CHECKS = 10;
const STATUS_CHECK_INTERVAL = 3000;

const PaymentStatus = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const merchantOrderId =
    searchParams.get("order_number") || searchParams.get("merchant_order_id");
  const [status, setStatus] = useState("VERIFYING");
  const [membership, setMembership] = useState(null);
  const [message, setMessage] = useState("");
  const [checking, setChecking] = useState(false);

  const verifyPayment = useCallback(async () => {
    if (!merchantOrderId) {
      setStatus("FAILED");
      setMessage("The payment reference is missing from the return URL.");
      return "FAILED";
    }

    setChecking(true);
    try {
      const response = await completePayment({
        merchant_order_id: merchantOrderId,
      });
      const result = response?.data || {};
      const nextStatus = String(result.status || "PENDING").toUpperCase();

      setStatus(nextStatus);
      if (nextStatus === "COMPLETED") {
        setMembership(result.membership || null);
        sessionStorage.removeItem("pellifix_pending_payment");
      }
      return nextStatus;
    } catch (error) {
      const httpStatus = error?.response?.status;
      if (!httpStatus || httpStatus >= 500) {
        setStatus("PENDING");
        setMessage("We could not confirm the payment yet. Please try again.");
        return "PENDING";
      }

      setStatus("FAILED");
      setMessage(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "The payment could not be verified.",
      );
      return "FAILED";
    } finally {
      setChecking(false);
    }
  }, [merchantOrderId]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let checks = 0;

    const checkStatus = async () => {
      checks += 1;
      const nextStatus = await verifyPayment();
      if (
        !cancelled &&
        nextStatus === "PENDING" &&
        checks < MAX_STATUS_CHECKS
      ) {
        timer = window.setTimeout(checkStatus, STATUS_CHECK_INTERVAL);
      }
    };

    checkStatus();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [verifyPayment]);

  const content = {
    VERIFYING: {
      icon: <CircularProgress size={36} thickness={4.5} />,
      title: "Verifying your payment",
      description: "Please wait while we confirm the payment with PhonePe.",
      color: "primary.main",
      background: "rgba(255, 128, 0, 0.1)",
    },
    PENDING: {
      icon: <PendingRoundedIcon sx={{ fontSize: 42, color: "warning.main" }} />,
      title: "Payment is processing",
      description:
        message ||
        "PhonePe has not confirmed the payment yet. We will keep checking.",
      color: "warning.main",
      background: "rgba(237, 108, 2, 0.1)",
    },
    COMPLETED: {
      icon: (
        <CheckCircleRoundedIcon sx={{ fontSize: 46, color: "success.main" }} />
      ),
      title: "Payment successful",
      description: membership?.ends_at
        ? "Your membership is active and ready to use."
        : "Your membership is now active.",
      color: "success.main",
      background: "rgba(46, 125, 50, 0.1)",
    },
    FAILED: {
      icon: <ErrorRoundedIcon sx={{ fontSize: 46, color: "error.main" }} />,
      title: "Payment verification failed",
      description: message || "We could not complete this payment.",
      color: "error.main",
      background: "rgba(211, 47, 47, 0.1)",
    },
  }[status] || {
    icon: <PendingRoundedIcon sx={{ fontSize: 42, color: "warning.main" }} />,
    title: "Payment is processing",
    description: "The payment has not reached a final status yet.",
    color: "warning.main",
    background: "rgba(237, 108, 2, 0.1)",
  };

  const formatDate = (value) =>
    value
      ? new Intl.DateTimeFormat("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "Asia/Kolkata",
        }).format(new Date(value))
      : "—";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 2, sm: 3 },
        background:
          "radial-gradient(circle at top, rgba(255,128,0,0.12), transparent 38%), #f7f8fb",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 580,
          overflow: "hidden",
          border: "1px solid rgba(15, 23, 42, 0.09)",
          borderRadius: 4,
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.11)",
        }}
      >
        {(status === "VERIFYING" || status === "PENDING") && (
          <LinearProgress
            color={status === "PENDING" ? "warning" : "primary"}
          />
        )}

        <Box sx={{ p: { xs: 3, sm: 4.5 }, textAlign: "center" }}>
          <Box
            sx={{
              width: 82,
              height: 82,
              display: "grid",
              placeItems: "center",
              mx: "auto",
              borderRadius: "50%",
              bgcolor: content.background,
              boxShadow: `0 0 0 10px ${content.background}`,
            }}
          >
            {content.icon}
          </Box>
          <Typography
            variant="h4"
            fontWeight={800}
            letterSpacing={-0.6}
            sx={{ mt: 3, mb: 1 }}
          >
            {content.title}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ maxWidth: 430, mx: "auto", lineHeight: 1.65 }}
          >
            {content.description}
          </Typography>

          {merchantOrderId && (
            <Box
              sx={{
                mt: 3,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,0.035)",
              }}
            >
              <Typography
                fontSize={11}
                color="text.secondary"
                fontWeight={700}
                letterSpacing={0.7}
              >
                PAYMENT REFERENCE
              </Typography>
              <Typography
                fontSize={13}
                fontWeight={650}
                sx={{ mt: 0.5, overflowWrap: "anywhere" }}
              >
                {merchantOrderId}
              </Typography>
            </Box>
          )}

          {status === "COMPLETED" && membership && (
            <Box
              sx={{
                mt: 3,
                textAlign: "left",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2.5,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography fontSize={12} color="text.secondary">
                    Membership status
                  </Typography>
                  <Typography fontWeight={750} sx={{ mt: 0.25 }}>
                    Membership activated
                  </Typography>
                </Box>
                <Chip
                  label={membership.status || "ACTIVE"}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 750 }}
                />
              </Box>
              <Divider />
              <Box
                sx={{
                  p: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <CalendarMonthRoundedIcon color="primary" fontSize="small" />
                  <Box>
                    <Typography fontSize={11} color="text.secondary">
                      Starts on
                    </Typography>
                    <Typography fontSize={13} fontWeight={650}>
                      {formatDate(membership.starts_at)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <CalendarMonthRoundedIcon color="primary" fontSize="small" />
                  <Box>
                    <Typography fontSize={11} color="text.secondary">
                      Valid until
                    </Typography>
                    <Typography fontSize={13} fontWeight={650}>
                      {formatDate(membership.ends_at)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 3.5,
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: 1.25,
              justifyContent: "center",
            }}
          >
            {status === "PENDING" && (
              <Button
                variant="outlined"
                size="large"
                startIcon={
                  checking ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshRoundedIcon />
                  )
                }
                disabled={checking}
                onClick={verifyPayment}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
              >
                {checking ? "Checking..." : "Check again"}
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={
                status === "COMPLETED" ? <HomeRoundedIcon /> : undefined
              }
              onClick={() =>
                navigate(
                  status === "COMPLETED" ? "/auth/home" : "/auth/subscribe",
                )
              }
              sx={{ textTransform: "none", fontWeight: 750, borderRadius: 2 }}
            >
              {status === "COMPLETED" ? "Go to home" : "Back to plans"}
            </Button>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <SecurityRoundedIcon color="success" sx={{ fontSize: 17 }} />
            <Typography fontSize={12} color="text.secondary">
              Secure payment verification with PhonePe
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentStatus;
